import useTranslator from "@pma/hooks/use-translator"
import { cn } from "@pma/utils/helpers"
import { ComponentPropsWithoutRef, ReactNode } from "react"
import { useFormContext } from "react-hook-form"

interface Option {
  value: string | number
  label: string
}

interface Props extends ComponentPropsWithoutRef<"select"> {
  name: string
  label?: string
  options: Option[]
  placeholder?: string
  required?: boolean
  className?: string
  includeNoOption?: boolean
}

export function SelectInput(props: Props) {
  const { translate, locale } = useTranslator()
  const isHebrew = locale === "he"
  const {
    name,
    label,
    options,
    placeholder = "Select an option",
    required = false,
    className = "",
    includeNoOption,
  } = props
  const {
    register,
    formState: { errors },
  } = useFormContext()
  function getNestedError(errors: any, name: string) {
    return name.split(".").reduce((obj, key) => obj?.[key], errors)
  }
  const error = getNestedError(errors, name)
  return (
    <div className='w-full space-y-1'>
      {label && (
        <label className={`text-sm font-medium text-gray-700 ${error ? "text-red-500" : ""}`}>
          {label} {required && <span className='text-red-500'>*</span>}
        </label>
      )}

      <div className='relative'>
        <select
          {...register(name, { valueAsNumber: true })}
          className={cn(
            "h-10 w-full appearance-none rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500",
            error ? "border-red-500 focus:ring-red-500" : "",
            className,
            isHebrew ? "pl-10" : "pr-10",
          )}
          {...props}
          required={false}
        >
          <option value={0} className='hidden'>
            {translate(placeholder)}
          </option>

          {/* Optional "No option" entry */}
          {includeNoOption && (
            <option disabled value='none'>
              {translate("No Option")}
            </option>
          )}

          {options.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>

        <div
          className={cn(
            "pointer-events-none absolute inset-y-0 flex items-center text-gray-500",
            isHebrew ? "left-2" : "right-2",
          )}
        >
          <svg
            className='h-4 w-4'
            fill='none'
            stroke='currentColor'
            strokeWidth={2}
            viewBox='0 0 24 24'
          >
            <path d='M19 9l-7 7-7-7' />
          </svg>
        </div>
      </div>

      {error && <p className='text-sm font-medium text-red-500'>{error?.message as ReactNode}</p>}
    </div>
  )
}
